import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { FileText, CheckCircle, XCircle, Clock, Trash2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { CardGuardUpload } from '../components/CardGuardUpload';

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200', labelKey: 'doc_status_pending' },
  approved: { icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', labelKey: 'doc_status_approved' },
  rejected: { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', labelKey: 'doc_status_rejected' },
};

export default function IncomeCertificatePage() {
  const { t } = useLanguage();
  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [annualIncome, setAnnualIncome] = useState('');
  const [monthlySalary, setMonthlySalary] = useState('');
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetchDoc(); }, []);

  const fetchDoc = async () => {
    try {
      const res = await api.getIncomeCert();
      if (res.data && res.data.status !== 'not_submitted') {
        setDoc(res.data);
      } else {
        setDoc(null);
        setShowForm(true);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.getIncomeCertHistory();
      setHistory(res.data || []);
    } catch (e) { console.error(e); }
  };

  const handleToggleHistory = () => {
    if (!showHistory && history.length === 0) fetchHistory();
    setShowHistory(!showHistory);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadDocument(file);
      setPhotoUrl(res.data.file_url);
      toast.success(t('doc_photo_uploaded'));
    } catch (err) {
      toast.error(t('doc_upload_fail'));
    } finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!photoUrl) { toast.error(t('doc_photo_required')); return; }
    setSubmitting(true);
    try {
      await api.submitIncomeCert({
        photo_url: photoUrl,
        annual_income: parseFloat(annualIncome),
        monthly_salary: parseFloat(monthlySalary),
      });
      toast.success(t('doc_submitted'));
      setShowForm(false);
      resetForm();
      fetchDoc();
      if (showHistory) fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('doc_submit_fail'));
    } finally { setSubmitting(false); }
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    resetForm();
  };

  const resetForm = () => {
    setPhotoUrl('');
    setAnnualIncome('');
    setMonthlySalary('');
  };

  const handleDelete = async () => {
    if (!window.confirm(t('doc_delete_confirm'))) return;
    try {
      await api.deleteIncomeCert();
      setDoc(null);
      setShowForm(true);
      toast.success(t('doc_deleted'));
      if (showHistory) fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('doc_delete_fail'));
    }
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const cfg = doc ? STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending : null;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="income-certificate-page">
        <h1 className="font-heading text-3xl font-bold text-primary mb-2">{t('income_cert_title')}</h1>
        <p className="text-muted-foreground mb-8">{t('income_cert_desc')}</p>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="space-y-6">
            {/* Current document display */}
            {doc && !showForm && (
              <>
                <div className="flex items-center justify-between">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium ${cfg.color}`} data-testid="income-cert-status">
                    <cfg.icon className="h-4 w-4" />
                    {t(cfg.labelKey)}
                  </div>
                  <Button onClick={handleAddNew} className="rounded-full" data-testid="add-new-income-cert-btn">
                    <Plus className="h-4 w-4 mr-2" />{t('doc_add_new')}
                  </Button>
                </div>

                <div className="bg-card border border-border/50 rounded-2xl p-6">
                  <Label className="mb-3 block font-semibold">{t('income_cert_photo')}</Label>
                  <img src={`${API_URL}${doc.photo_url}`} alt="Income Certificate" className="w-full max-h-72 object-contain rounded-lg border" data-testid="income-cert-image" />
                </div>

                <div className="bg-card border border-border/50 rounded-2xl p-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t('income_cert_annual')}</span>
                    <span className="font-semibold text-lg">JPY {doc.annual_income?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t('income_cert_monthly')}</span>
                    <span className="font-semibold text-lg">JPY {doc.monthly_salary?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t('doc_submitted_at')}</span>
                    <span className="text-sm">{new Date(doc.submitted_at).toLocaleDateString()}</span>
                  </div>
                  {doc.reviewer_notes && (
                    <div className="border-t pt-3 mt-3">
                      <span className="text-muted-foreground text-sm">{t('doc_reviewer_notes')}: </span>
                      <span className="text-sm">{doc.reviewer_notes}</span>
                    </div>
                  )}
                </div>

                {false && (doc.status === 'pending' || doc.status === 'rejected') && (
                  <Button variant="destructive" onClick={handleDelete} className="rounded-full" data-testid="delete-income-cert-btn">
                    <Trash2 className="h-4 w-4 mr-2" />{t('doc_delete_resubmit')}
                  </Button>
                )}
              </>
            )}

            {/* Submission form */}
            {showForm && (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="income-cert-form">
                {doc && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700" data-testid="income-cert-new-banner">
                    {t('doc_add_new')} - {t('income_cert_title')}
                  </div>
                )}
                <div className="bg-card border border-border/50 rounded-2xl p-6">
                  <Label className="mb-3 block font-semibold">{t('income_cert_photo')} *</Label>
                  <CardGuardUpload
                    label={t('doc_click_upload')}
                    uploading={uploading}
                    previewUrl={photoUrl ? `${API_URL}${photoUrl}` : null}
                    onUpload={(file) => handleUpload({ target: { files: [file] } })}
                    onRemove={() => setPhotoUrl('')}
                    testId="income-cert-upload"
                  />
                </div>

                <div className="bg-card border border-border/50 rounded-2xl p-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label>{t('income_cert_annual')} (JPY) *</Label>
                    <Input type="number" value={annualIncome} onChange={e => setAnnualIncome(e.target.value)} placeholder="5,000,000" required data-testid="annual-income-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('income_cert_monthly')} (JPY) *</Label>
                    <Input type="number" value={monthlySalary} onChange={e => setMonthlySalary(e.target.value)} placeholder="416,000" required data-testid="monthly-salary-input" />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={submitting || !photoUrl} className="flex-1 rounded-full" data-testid="submit-income-cert-btn">
                    <FileText className="h-4 w-4 mr-2" />{submitting ? t('doc_submitting') : t('doc_submit')}
                  </Button>
                  {doc && (
                    <Button type="button" variant="outline" onClick={handleCancelForm} className="rounded-full" data-testid="cancel-new-income-btn">
                      {t('cancel')}
                    </Button>
                  )}
                </div>
              </form>
            )}

            {/* History section */}
            {doc && (
              <div>
                <button
                  onClick={handleToggleHistory}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-income-history"
                >
                  {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {t('doc_history')}
                </button>
                {showHistory && (
                  <div className="mt-3 space-y-3" data-testid="income-cert-history">
                    {history.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('loading')}</p>
                    ) : history.map((item, idx) => {
                      const s = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                      return (
                        <div key={item.id} className="bg-card border border-border/50 rounded-xl p-4 flex items-center justify-between" data-testid={`history-item-${idx}`}>
                          <div>
                            <div className="text-sm font-medium">JPY {item.annual_income?.toLocaleString()}</div>
                            <div className="text-xs text-muted-foreground">{new Date(item.submitted_at).toLocaleDateString()}</div>
                          </div>
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${s.color}`}>
                            <s.icon className="h-3 w-3" />
                            {t(s.labelKey)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
